import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import * as _ from 'lodash';

import { TableComponent } from 'shared/table/table/table.component';
import { Core } from './core.model';
import * as utils from 'utils/utils';
import {ProductPanelComponent} from '../../../../product-panel/product-panel.component';
import { rowData, contentData, collapseWidthPx } from './table-data';
import {DomSanitizer} from '@angular/platform-browser';
import {CollapseGroupComponent} from 'shared/collapse/collapse-group/collapse-group.component';
import {LoaderComponent} from 'shared/loader/loader.component';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import { TableEditableComponent } from 'shared/table/table-editable/table-editable.component';



@Component({
  moduleId: module.id,
  selector: 'core-form',
  templateUrl: 'core-form.component.html',
  styleUrls: ['core-form.component.scss'],
})
export class CoreFormComponent extends TableComponent implements OnChanges, OnInit, OnDestroy {

  headers: any[] = ['name', 'class', 'group', 'days'];

  form: UntypedFormGroup;

  @Input() segmentData: Array<any> = [];
  @Input() declare data: any;
  @Input() savingEvents: Observable<void>;

  @ViewChildren(CollapseGroupComponent) collapseGroups: QueryList<CollapseGroupComponent>;
  @ViewChild(LoaderComponent, {static: true})
  loader: LoaderComponent;

  @ViewChild(TableEditableComponent, {static: false}) tableEditable: TableEditableComponent | void;
  rowData: any[] = rowData;
  contentData: any[] = contentData;
  collapseWidth = collapseWidthPx;
  controlClassPrefix: string = 'core-control-row-';
  editMode: boolean = false;

  @Output() cancel: EventEmitter<any> = new EventEmitter();
  @Output() saveChanges: EventEmitter<any> = new EventEmitter();

  private savingEventsSubscription: Subscription;

  get core(): UntypedFormArray {
    return this.form.get('core') as UntypedFormArray;
  }

  get isInValid() {
    return this.form.invalid;
  }

  get isEmpty(): boolean {
    return !_.get(this.core, 'controls.length');
  }

  get formArray(): UntypedFormArray {
    return this.core;
  }

  get isEditMode(): boolean {
    return this.editMode;
  }

  set isEditMode(value: boolean) {
    this.editMode = value;
  }

  toggleEditMode() {
    this.editMode = !this.editMode;
  }

  get isFormChanged(): boolean {
    if (this.isEditMode) {
      return !!this.form.dirty;
    } else {
      return false;
    }
  }

  constructor(
    private fb: UntypedFormBuilder,
    private ref: ChangeDetectorRef,
    protected sanitizer: DomSanitizer,
  ) {
    super();
    this.form = this.fb.group({
      core: this.fb.array([])
    });
  }

  ngOnInit() {
    this.savingEventsSubscription = this.savingEvents.subscribe(() => {
      this.editMode = false;
    });
  }

  ngOnChanges() {
    this.setData();
  }

  ngOnDestroy() {
    this.savingEventsSubscription.unsubscribe();
  }

  setData() {
    this.setFormArray(this.data, this.segmentData);
  }

  setFormArray(data: any, segmentData: any): void {
    if (!this.data) { return; }
    const formGroups = _.map(data, (item: any) => {
      const model = utils.populateModel(Core, segmentData, item);
      return this.fb.group(model);
    });
    this.form.setControl('core', this.fb.array(formGroups));
  }

  public getField(fieldMap: string): any {
    // @ts-ignore
    return _.find(this.segmentData, {field_map: fieldMap});
  }

  public fieldValidation(item: any, formGroup: UntypedFormGroup): any {
    if (!item) {
      return;
    }
    let validation = super.fieldValidation(item, formGroup);
    // PIES 6.7 validation row was added for test
    const pies65: any = _.find(validation, {name: 'PIES_6_5'});
    if (pies65) {
      validation = [...validation, {
        key: 'pies67',
        title: 'PIES 6.7',
        valid: pies65.valid,
      }];
    }
    return validation;
  }

  public fieldHintText(name: string): string {
    const item = this.getField(name);
    return item && item.hint_detail;
  }

  protected getTitle(name: string): string {
    // find title in rowData
    // @ts-ignore
    const colData = _.find(this.rowData, { fieldName: name });
    if (_.get(colData, 'title')) {
      return colData.title;
    }

    // find title in segmentData
    // @ts-ignore
    const item = _.find(this.segmentData, { field_map: name });
    return item ? item.title : '';
  }

  protected getInvalidDot(fieldMap: string, formGroup: UntypedFormGroup, index: number) {
    const control = _.get(formGroup, `controls.${fieldMap}`);
    if (control.invalid) {
      return   this.sanitizer.bypassSecurityTrustHtml(`
        <div class="circle-icon size-small cell-invalid-dot circle-red"
        data-ghost="cell-invalid-dot-${fieldMap}-${index}"></div>
      `);
    } else {
      return null;
    }
  }

  protected collapseRow(index: number, collapse?: boolean) {
    // pass -1 to collapse all
    const collapseGroupsArr: any = this.collapseGroups.toArray();

    if (!_.get(collapseGroupsArr, 'length') || !_.get(collapseGroupsArr, `[${index}]`)) { return; }
    if (_.isNil(collapse)) { collapse = !collapseGroupsArr[index].collapsed; }
    collapseGroupsArr[index].setCollapsed(collapse);

    this.ref.markForCheck();
  }

  // handlers
  onAddAnotherClick() {
    const model = utils.populateModel(Core, this.segmentData, {});
    this.core.push(this.fb.group(model));
    this.form.markAsDirty();
  }

  onRowRemove(formGroup: UntypedFormGroup) {
    const index = this.formArray.controls.indexOf(formGroup);
    this.formArray.removeAt(index);
    this.data.splice(index, 1);
    this.formArray.markAsDirty();
  }

  onCollapseClick(index: number) {
    this.collapseRow(index);
  }

  onViewEditSaveClick() {
    if (this.isFormChanged) {
      this.saveChanges.emit();
    } else {
      this.toggleEditMode();
    }
  }

  onCancelClick() {
    this.cancel.emit();
  }

  onRowAdd() {
    this.onAddAnotherClick();
  }
}
